import {isDevEnvironment} from './utils';

export type ServiceEnvironment =
  | 'tunnel-devbox'
  | 'devbox'
  | 'qa'
  | 'preprod'
  | 'prod';

export interface IScriptUrlContext {
  origin: string;
  absoluteFolderPath: string;
  serviceEnvironment: ServiceEnvironment;
}

let result: IScriptUrlContext | undefined;

const getServiceEnvironmentFromUrl = (url: URL): ServiceEnvironment => {
  const origin = url.host.toLowerCase();
  const USER = process.env.STRIPE_USER;

  if (
    origin.startsWith('localhost') ||
    origin.startsWith('127.0.0.1') ||
    (USER && origin.includes(String(USER)))
  ) {
    if (origin.endsWith('tunnel.stripe.me')) {
      return 'tunnel-devbox';
    }

    return 'devbox';
  } else if (origin.startsWith('qa-')) {
    return 'qa';
  } else if (origin.startsWith('preprod-') || origin.startsWith('edge-')) {
    return 'preprod';
  } else {
    return 'prod';
  }
};

export const initCurrentScriptUrlContext = (
  currentScript: HTMLOrSVGScriptElement | string | null = document.location
    .origin,
  isDevEnv: boolean = isDevEnvironment(),
) => {
  if (result && !currentScript) {
    return result;
  }
  const currentScriptUrl =
    typeof currentScript === 'string'
      ? currentScript.replace(/^file:\/\//, '')
      : currentScript?.getAttribute('src');
  if (!currentScriptUrl) {
    throw new Error(`currentScriptUrl is not defined: ${currentScriptUrl}`);
  }

  const url = new URL(currentScriptUrl, window.location.href);
  const folderPathComponents = url.pathname.split('/');
  folderPathComponents.pop();
  const folderPath = folderPathComponents.join('/');
  let {origin} = url;
  if (isDevEnv) {
    // When running locally, we intentionally replace the data layer URL with 127.0.0.1 to force different origins between the
    // host and the data layer. This helps to catch issues stemming from differing origins between the host and data layer earlier
    // in the development process (makes local dev ~= production)
    origin = url.origin.replace('localhost', '127.0.0.1');
  }
  result = {
    origin,
    absoluteFolderPath: origin + folderPath,
    serviceEnvironment: getServiceEnvironmentFromUrl(url),
  };

  return result;
};

export const getCurrentScriptUrlContext = () => {
  if (!result) {
    result = initCurrentScriptUrlContext();
  }
  return result;
};

export const getServiceEnvironment = () => {
  if (!result) {
    result = initCurrentScriptUrlContext();
  }

  return result.serviceEnvironment;
};
